import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AccountButton from "./AccountButton";
import AccountInput from "./AccountInput";
import { loginUser } from "./api/apiFunctions";
import { t } from "./i18n";
import { setAuthToken, setUserData } from "./utils/auth";
import VerificationModal from "./VerificationModal";
import DashboardConfirmationModal from "./DashboardConfirmationModal";
import CreateCardModal from "./CreateCardModal";
import ForgotPasswordModal from "./ForgotPasswordModal";

export default function LoginPage({ lang = "en" }) {
  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showCreateCardModal, setShowCreateCardModal] = useState(false);
  const [showResendVerificationModal, setShowResendVerificationModal] =
    useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [pendingRole, setPendingRole] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const handleDashboardModalClose = () => {
    setShowDashboardModal(false);
    if (pendingRole === "patient") {
      setShowCreateCardModal(true);
    } else if (pendingRole === "doctor") {
      navigate("/doctor-dashboard");
    }
  };

  const handleVerification = (verificationCode) => {
    if (verificationCode.length === 6) {
      setShowVerificationModal(false);
      if (pendingRole === "patient") {
        setShowDashboardModal(true);
      } else if (pendingRole === "doctor") {
        navigate("/doctor-dashboard");
      }
    }
  };

  const handleCardSubmit = (data, cardResponse, qrResponse) => {
    setShowCreateCardModal(false);

    // If API call was successful, you might want to store the card data or handle it differently
    if (cardResponse) {
      // You can store the card ID or other response data if needed
      localStorage.setItem(
        "patientCardId",
        cardResponse.id || cardResponse.cardId,
      );
    }

    // Store QR code data if generated successfully
    if (qrResponse) {
      localStorage.setItem("patientQRCode", JSON.stringify(qrResponse));
    }

    // After SAL card creation, start subscription flow instead of going
    // directly to the dashboard.
    navigate("/add-subscription");
  };

  // Check for inactivity message from location state
  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
      // Clear the state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) {
      setError(t("loginErrorRequired", lang));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setError("Please enter a valid email address");
      return;
    }
    setIsLoading(true);
    try {
      const response = await loginUser({
        email: form.email.toLowerCase().trim(),
        password: form.password,
      });
      if (response.success) {
        setAuthToken(response.token);
        setUserData(response.data);
        setPendingRole(response.data.role);
        if (response.verificationRequired) {
          setShowVerificationModal(true);
          setShowResendVerificationModal(true);
          return;
        }
        if (response.data.role === "doctor") {
          navigate("/doctor-dashboard");
        } else {
          navigate("/add-subscription");
        }
      } else if (response.verificationRequired) {
        setShowVerificationModal(true);
        setShowResendVerificationModal(true);
      } else {
        setError(response.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      setError(
        error.message || "An error occurred during login. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f7f8fa] to-[#e6e9f5] py-5">
      <div className="w-11/12 sm:w-full sm:max-w-md bg-white rounded-2xl shadow-xl p-10 border border-[#e5e7eb]">
        <h2 className="text-3xl font-extrabold text-[#2d348f] mb-8 text-center tracking-tight">
          {t("loginTitle", lang)}
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <AccountInput
            label={t("loginEmail", lang)}
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            disabled={isLoading}
          />
          <AccountInput
            label={t("loginPassword", lang)}
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            showPasswordToggle={true}
            disabled={isLoading}
          />
          <div className="flex justify-end -mt-3">
            <button
              type="button"
              onClick={() => setShowForgotPasswordModal(true)}
              className="text-xs text-[#4962d8] hover:underline focus:outline-none"
            >
              Forgot Password?
            </button>
          </div>
          {error && <div className="text-red-500 text-sm">*{error}</div>}
          <AccountButton
            type="submit"
            className="mt-2 mb-1 rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Logging in...
              </div>
            ) : (
              t("loginButton", lang)
            )}
          </AccountButton>
        </form>
        <VerificationModal
          open={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onVerify={handleVerification}
          email={form.email}
          lang={lang}
          firstName={form?.name?.split(" ")[0] ?? ""}
          showResend={showResendVerificationModal}
        />
        <DashboardConfirmationModal
          open={showDashboardModal}
          firstName={form?.name?.split(" ")[0] ?? ""}
          onNavigate={handleDashboardModalClose}
          lang={lang}
        />
        <CreateCardModal
          open={showCreateCardModal}
          onClose={() => setShowCreateCardModal(false)}
          onSubmit={handleCardSubmit}
          lang={lang}
        />
        <div className="mt-6 text-center text-sm text-[#837f90]">
          {t("loginNoAccount", lang)}{" "}
          <Link
            to="/signup"
            className="text-[#2d348f] font-semibold hover:underline"
          >
            {t("loginSignup", lang)}
          </Link>
        </div>
      </div>
      <ForgotPasswordModal
        open={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
}
